/**
 * MessageRegenerate — "run this prompt again", the second control in a settled
 * assistant turn's actions rail, sized to sit beside `CopyMessageButton`.
 *
 * A SIBLING of `MessageRetry`, not a variant of it, because the two answer
 * different questions. `MessageRetry` belongs to a USER message that never
 * landed (connection dropped, stream errored, 5xx) and offers to send it again.
 * This belongs to an ASSISTANT message that landed fine and offers a different
 * answer to the same prompt. They live at opposite ends of a turn, carry
 * opposite voices (a failure notice vs. a quiet icon), and change for different
 * reasons, so they are two components.
 *
 * Disabled rather than hidden while sending is impossible. The rail is
 * hover-revealed and only a couple of controls wide, so dropping one shifts the
 * other under the cursor mid-hover — the control stays put and greys out, and
 * `disabledReason` gives the consumer's tooltip something honest to say.
 *
 * The component owns only the affordance. What "regenerate" means is the
 * caller's: most hosts replay the original prompt server-side against the
 * message id rather than re-sending from the composer, so no duplicate user
 * bubble appears and a reload agrees with the live transcript. Which turns are
 * eligible is the caller's too — typically only the newest assistant message,
 * since replaying mid-history would leave later turns referencing an answer that
 * no longer exists.
 *
 * No built-in tooltip, per the `ActionIconTile` precedent: a real `aria-label`
 * here, the hint in the consumer's tooltip, one string in one place.
 *
 *   <MessageRegenerate
 *     onRegenerate={() => regenerate(m.id)}
 *     disabled={!canSend}
 *     disabledReason="Wait for the current reply to finish"
 *   />
 */
import * as React from "react";
import { RotateCcw } from "lucide-react";
import { cn } from "../lib/cn";
import { Spinner } from "../primitives/Spinner";

export interface MessageRegenerateProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "children"
> {
  /** Re-run the prompt this answer came from. */
  onRegenerate: () => void;
  /**
   * Why the control is disabled, for the consumer's tooltip. Not rendered here
   * — it is returned on `data-disabled-reason` so a tooltip can read it.
   */
  disabledReason?: string;
  /** Accessible name. Default `"Regenerate response"`. */
  label?: string;
  /**
   * A regeneration is in flight: swaps the icon for a `<Spinner>` and
   * force-disables, so the same turn cannot be queued twice. Mirrors
   * `MessageRetry`'s `isRetrying` exactly, since the two sit in the same rail
   * and a reader should not have to learn two busy languages.
   */
  isRegenerating?: boolean;
}

export const MessageRegenerate = React.forwardRef<HTMLButtonElement, MessageRegenerateProps>(
  function MessageRegenerate(
    {
      onRegenerate,
      disabled,
      disabledReason,
      label = "Regenerate response",
      isRegenerating = false,
      className,
      onClick,
      ...props
    },
    ref,
  ) {
    /*
     * COMPOSED, and `{...props}` is spread BEFORE it. Both halves are
     * load-bearing: a Radix `asChild` trigger (Tooltip, Popover) clones this
     * element and injects its OWN onClick, so a trailing spread silently replaces
     * the regenerate handler and the button renders perfectly while doing nothing.
     * Spread first, then compose, so the parent's handler runs and so does ours.
     */
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);
      onRegenerate();
    };

    return (
      <button
        ref={ref}
        type="button"
        {...props}
        onClick={handleClick}
        disabled={disabled || isRegenerating}
        aria-busy={isRegenerating || undefined}
        aria-label={label}
        data-disabled-reason={disabled ? disabledReason : undefined}
        /*
         * The same rest + hover-background treatment as CopyMessageButton, and it
         * has to match: the two sit adjacent in one rail. The disabled variants
         * cancel the hover so a control you cannot use never lights up under the
         * cursor.
         */
        className={cn(
          "inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground",
          "transition-colors hover:bg-muted hover:text-foreground",
          "focus-visible:bg-muted focus-visible:text-foreground focus-visible:outline-none",
          "disabled:cursor-not-allowed disabled:opacity-40",
          "disabled:hover:bg-transparent disabled:hover:text-muted-foreground",
          className,
        )}
      >
        {isRegenerating ? (
          <Spinner className="h-3.5 w-3.5" decorative />
        ) : (
          <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
        )}
      </button>
    );
  },
);
